import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import DataTable, { type DataTableColumn } from '@shared/components/DataTable';
import CardSection from '@shared/components/CardSection';
import PanelSkeleton from '@shared/components/PanelSkeleton';
import { useTransferHistory } from '../hooks/useTransferHistory';
import type { Transfer } from '../types';
import type { OperationStatus } from '@shared/types';

const statusColorMap: Record<
  OperationStatus,
  'default' | 'info' | 'success' | 'error' | 'warning'
> = {
  queued: 'default',
  running: 'info',
  succeeded: 'success',
  failed: 'error',
  partial: 'warning',
  cancelled: 'warning',
};

function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const columns: DataTableColumn<Transfer>[] = [
  {
    id: 'operationId',
    label: 'Operation',
    width: 130,
    render: (row) => row.operationId,
  },
  {
    id: 'status',
    label: 'Status',
    width: 100,
    render: (row) => (
      <Chip
        label={row.status}
        color={statusColorMap[row.status]}
        size="small"
        sx={{ textTransform: 'capitalize' }}
      />
    ),
  },
  {
    id: 'targets',
    label: 'Targets',
    width: 80,
    render: (row) => row.targets.length,
  },
  {
    id: 'progress',
    label: 'Progress',
    width: 80,
    render: (row) => `${row.progressPercent}%`,
  },
  {
    id: 'started',
    label: 'Started',
    width: 120,
    render: (row) => formatDate(row.startedAtUtc),
  },
  {
    id: 'finished',
    label: 'Finished',
    width: 120,
    render: (row) => formatDate(row.finishedAtUtc),
  },
];

export default function TransferHistory() {
  const { data: transfers, isLoading } = useTransferHistory();

  if (isLoading) return <PanelSkeleton />;

  return (
    <div data-testid="transfer-history">
      <CardSection title="Transfer History">
        {!transfers || transfers.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No previous transfers.
          </Typography>
        ) : (
          <DataTable
            columns={columns}
            rows={transfers}
            rowKey={(row) => row.operationId}
            emptyMessage="No transfers"
          />
        )}
      </CardSection>
    </div>
  );
}
